import { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Calendar,
  RefreshCw,
  Download,
  Eye,
  CalendarDays,
  PartyPopper,
  X,
  Copy,
  Library,
  LayoutGrid,
  List as ListIcon,
  Power
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import apiClient from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface Holiday {
  date: string;
  name: string;
  recurring: boolean;
  action?: {
    type: 'extension' | 'voicemail' | 'queue' | 'ivr' | 'hangup';
    destination: string;
  };
}

interface HolidayCalendar {
  id: string;
  name: string;
  description?: string;
  is_template: boolean;
  holidays: Holiday[];
  enabled: boolean;
  created_at: Date;
  updated_at: Date;
}

export default function HolidayCalendars() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [calendars, setCalendars] = useState<HolidayCalendar[]>([]);
  const [templates, setTemplates] = useState<HolidayCalendar[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'create' | 'edit' | 'view'>('create');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<Partial<HolidayCalendar>>({});
  const [editingItem, setEditingItem] = useState<HolidayCalendar | null>(null);
  const [newHoliday, setNewHoliday] = useState<Partial<Holiday>>({ recurring: true });
  const [showTemplates, setShowTemplates] = useState(false);
  const [copyingTemplate, setCopyingTemplate] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const loadCalendars = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/holiday-calendars');
      if (response?.success && Array.isArray(response?.data)) {
        setCalendars(response.data);
      } else {
        setCalendars([]);
      }
    } catch (error) {
      console.error('Error loading calendars:', error);
      setCalendars([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await apiClient.get('/holiday-calendars/templates');
      if (response?.success && Array.isArray(response?.data)) {
        setTemplates(response.data);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    }
  };

  useEffect(() => {
    loadCalendars();
    loadTemplates();
  }, []);

  const copyTemplate = async (templateId: string, customName?: string) => {
    setCopyingTemplate(templateId);
    try {
      const response = await apiClient.post(`/holiday-calendars/${templateId}/copy`, {
        name: customName
      });
      if (response?.success) {
        await loadCalendars();
        setShowTemplates(false);
        toast({ title: "Copiato", description: "Template copiato con successo" });
      }
    } catch (error) {
      console.error('Error copying template:', error);
      toast({ title: "Errore", description: "Errore durante la copia del template", variant: "destructive" });
    } finally {
      setCopyingTemplate(null);
    }
  };

  const loadItalianPreset = async () => {
    try {
      const response = await apiClient.get('/holiday-calendars/presets/italian');
      if (response?.success && Array.isArray(response?.data)) {
        setFormData({
          ...formData,
          holidays: response.data
        });
        toast({ title: "Caricato", description: "Festività italiane caricate" });
      }
    } catch (error) {
      console.error('Error loading Italian preset:', error);
    }
  };

  const handleCreate = async () => {
    if (!user?.tenant_id) return;
    if (!formData.name?.trim()) {
      toast({ title: "Errore", description: "Il nome è obbligatorio", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/holiday-calendars', {
        ...formData,
        tenant_id: user.tenant_id
      });

      if (response?.success) {
        await loadCalendars();
        setShowModal(false);
        setFormData({});
        toast({ title: "Creato", description: "Calendario creato con successo" });
      }
    } catch (error) {
      console.error('Error creating calendar:', error);
      toast({ title: "Errore", description: "Errore durante la creazione", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editingItem?.id) return;

    setLoading(true);
    try {
      const response = await apiClient.put(`/holiday-calendars/${editingItem.id}`, formData);

      if (response?.success) {
        await loadCalendars();
        setShowModal(false);
        setFormData({});
        setEditingItem(null);
        toast({ title: "Aggiornato", description: "Calendario aggiornato con successo" });
      }
    } catch (error) {
      console.error('Error updating calendar:', error);
      toast({ title: "Errore", description: "Errore durante l'aggiornamento", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (calendar: HolidayCalendar) => {
    if (!confirm(`Eliminare il calendario "${calendar.name}"?`)) return;

    setLoading(true);
    try {
      await apiClient.delete(`/holiday-calendars/${calendar.id}`);
      await loadCalendars();
      toast({ title: "Eliminato", description: "Calendario eliminato con successo" });
    } catch (error) {
      console.error('Error deleting calendar:', error);
      toast({ title: "Errore", description: "Errore durante l'eliminazione", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (calendar: HolidayCalendar) => {
    setLoading(true);
    try {
      await apiClient.put(`/holiday-calendars/${calendar.id}`, { enabled: !calendar.enabled });
      await loadCalendars();
      toast({ 
        title: calendar.enabled ? "Disabilitato" : "Abilitato", 
        description: `Calendario ${calendar.enabled ? 'disabilitato' : 'abilitato'} con successo` 
      });
    } catch (error) {
      console.error('Error toggling calendar:', error);
      toast({ title: "Errore", description: "Errore durante l'aggiornamento", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const addHoliday = () => {
    if (!newHoliday.date || !newHoliday.name) return;
    
    const holidays = [...(formData.holidays || []), {
      date: newHoliday.date,
      name: newHoliday.name,
      recurring: newHoliday.recurring ?? true
    }];
    
    setFormData({ ...formData, holidays });
    setNewHoliday({ recurring: true });
  };

  const removeHoliday = (index: number) => {
    const holidays = [...(formData.holidays || [])];
    holidays.splice(index, 1);
    setFormData({ ...formData, holidays });
  };

  const filteredCalendars = calendars.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  const getNextHoliday = (holidays: Holiday[]) => {
    const today = new Date();
    const todayStr = today.toISOString().slice(5, 10);
    
    const sortedHolidays = [...holidays]
      .map(h => ({
        ...h,
        monthDay: h.date.slice(5, 10)
      }))
      .sort((a, b) => a.monthDay.localeCompare(b.monthDay));
    
    const upcoming = sortedHolidays.find(h => h.monthDay >= todayStr);
    return upcoming || sortedHolidays[0];
  };

  const openCreateModal = () => {
    setModalType('create');
    setEditingItem(null);
    setFormData({ holidays: [], enabled: true });
    setShowModal(true);
  };

  const openEditModal = (calendar: HolidayCalendar) => {
    setModalType('edit');
    setFormData(calendar);
    setEditingItem(calendar);
    setShowModal(true);
  };

  const openViewModal = (calendar: HolidayCalendar) => {
    setModalType('view');
    setFormData(calendar);
    setEditingItem(calendar);
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Search Bar + View Toggle */}
      <div className="relative z-10 flex items-center justify-between">
        <div className="flex items-center space-x-3 bg-muted/20 p-2 rounded-xl border border-border backdrop-blur-md w-fit">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca calendari..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-transparent border-none text-foreground placeholder:text-muted-foreground h-9"
            />
          </div>
          <div className="h-6 w-px bg-border" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setViewMode('grid')}
            className={`h-8 w-8 ${viewMode === 'grid' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setViewMode('list')}
            className={`h-8 w-8 ${viewMode === 'list' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
          >
            <ListIcon className="h-4 w-4" />
          </Button>
          {templates.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowTemplates(true)} className="text-muted-foreground hover:text-foreground h-9">
              <Library className="h-4 w-4 mr-2" />
              Template ({templates.length})
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={loadCalendars} disabled={loading} className="text-muted-foreground hover:text-foreground hover:bg-muted/50 h-9">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Ricarica
          </Button>
        </div>

        <Button onClick={openCreateModal} className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg">
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Calendario
        </Button>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' ? (
        <div className="relative z-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredCalendars.map((calendar) => {
            const nextHoliday = getNextHoliday(calendar.holidays);
            return (
              <Card
                key={calendar.id}
                className="glass-card-enhanced group hover:-translate-y-1 transition-all duration-300 border border-border bg-card text-card-foreground"
              >
                <CardHeader className="pb-3 border-b border-border">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1 flex-1 min-w-0">
                      <CardTitle className="text-xl font-bold text-foreground tracking-tight flex items-center gap-2">
                        <PartyPopper className="h-5 w-5 text-amber-500 flex-shrink-0" />
                        <span className="truncate">{calendar.name}</span>
                        {!calendar.enabled && (
                          <div className="h-2 w-2 rounded-full bg-destructive/50 animate-pulse flex-shrink-0" title="Disabilitato" />
                        )}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-xs font-medium uppercase tracking-wider truncate">
                        {calendar.description || 'Nessuna descrizione'}
                      </CardDescription>
                    </div>
                    <Badge
                      variant={calendar.enabled ? 'default' : 'secondary'}
                      className={`
                        border-0 shadow-sm backdrop-blur-md flex-shrink-0
                        ${calendar.enabled
                          ? 'bg-green-500/20 text-green-600 dark:text-green-300 ring-1 ring-green-500/30'
                          : 'bg-destructive/10 text-destructive ring-1 ring-destructive/20'}
                      `}
                    >
                      {calendar.enabled ? 'Attivo' : 'Inattivo'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="space-y-3 text-sm">
                    {/* Holidays Count */}
                    <div className="flex justify-between items-center p-2 rounded-lg bg-muted/30 border border-border">
                      <span className="text-muted-foreground text-xs uppercase tracking-wider">Festività</span>
                      <span className="font-mono text-foreground font-medium">{calendar.holidays.length}</span>
                    </div>

                    {/* Next Holiday */}
                    {nextHoliday && (
                      <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                        <span className="block text-muted-foreground text-[10px] uppercase">Prossima Festività</span>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-foreground font-medium text-sm truncate">{nextHoliday.name}</span>
                          <span className="font-mono text-amber-600 dark:text-amber-400 text-xs">{formatDate(nextHoliday.date)}</span>
                        </div>
                      </div>
                    )}

                    {/* Holidays Preview */}
                    <div className="space-y-1">
                      {calendar.holidays.slice(0, 3).map((h, index) => (
                        <div key={index} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-muted/10">
                          <span className="text-muted-foreground truncate">{h.name}</span>
                          <span className="font-mono text-foreground">{formatDate(h.date)}</span>
                        </div>
                      ))}
                      {calendar.holidays.length > 3 && (
                        <div className="text-[10px] text-muted-foreground text-center">
                          +{calendar.holidays.length - 3} altre festività
                        </div>
                      )}
                    </div>

                    {/* Template Badge */}
                    {calendar.is_template && (
                      <Badge variant="outline" className="text-[10px] h-5 bg-purple-500/10 text-purple-600 border-purple-200 dark:border-purple-800">
                        <Library className="h-3 w-3 mr-1" />
                        Template
                      </Badge>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openViewModal(calendar)}
                      disabled={loading}
                      className="col-span-1 h-9 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20"
                      title="Visualizza"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(calendar)}
                      disabled={loading}
                      className="col-span-1 h-9 bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/20"
                      title="Modifica"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleEnabled(calendar)}
                      disabled={loading}
                      className={`col-span-1 h-9 border ${calendar.enabled ? 'bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/20' : 'bg-muted text-muted-foreground border-border'}`}
                      title={calendar.enabled ? 'Disabilita' : 'Abilita'}
                    >
                      <Power className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(calendar)}
                      disabled={loading}
                      className="col-span-1 h-9 bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/20"
                      title="Elimina"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="relative z-10 space-y-2">
          {filteredCalendars.map((calendar) => {
            const nextHoliday = getNextHoliday(calendar.holidays);
            return (
              <div
                key={calendar.id}
                className="glass-card flex items-center justify-between p-4 rounded-lg border border-border bg-card/50 hover:bg-card transition-colors"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-2 h-10 rounded-full ${calendar.enabled ? 'bg-green-500' : 'bg-destructive/50'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <PartyPopper className="h-4 w-4 text-amber-500" />
                      <span className="text-lg font-bold text-foreground">{calendar.name}</span>
                      {!calendar.enabled && (
                        <div className="h-2 w-2 rounded-full bg-destructive/50 animate-pulse" title="Disabilitato" />
                      )}
                      {calendar.is_template && (
                        <Badge variant="outline" className="text-[9px] h-4 bg-purple-500/10 text-purple-600 border-purple-200">
                          Template
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                      <span>{calendar.holidays.length} festività</span>
                      {nextHoliday && (
                        <>
                          <span>|</span>
                          <span>Prossima: <span className="text-amber-600">{nextHoliday.name}</span> ({formatDate(nextHoliday.date)})</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={calendar.enabled ? 'default' : 'secondary'}
                    className={`text-[10px] ${calendar.enabled
                      ? 'bg-green-500/20 text-green-600 ring-1 ring-green-500/30'
                      : 'bg-destructive/10 text-destructive ring-1 ring-destructive/20'
                    }`}
                  >
                    {calendar.enabled ? 'Attivo' : 'Inattivo'}
                  </Badge>
                  <Button variant="ghost" size="icon" onClick={() => openViewModal(calendar)} className="h-8 w-8 text-cyan-600" title="Visualizza">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEditModal(calendar)} className="h-8 w-8 text-blue-600" title="Modifica">
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleToggleEnabled(calendar)} className={`h-8 w-8 ${calendar.enabled ? 'text-green-600' : 'text-muted-foreground'}`} title={calendar.enabled ? 'Disabilita' : 'Abilita'}>
                    <Power className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(calendar)} className="h-8 w-8 text-destructive" title="Elimina">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {filteredCalendars.length === 0 && !loading && (
        <Card className="border border-border bg-card">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <PartyPopper className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Nessun Calendario</h3>
            <p className="text-muted-foreground text-center mb-4">
              {searchTerm ? 'Nessun calendario corrisponde alla ricerca.' : 'Crea il tuo primo calendario festività.'}
            </p>
            {!searchTerm && (
              <Button onClick={openCreateModal}>
                <Plus className="h-4 w-4 mr-2" />
                Crea Calendario
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Templates Modal */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-card text-card-foreground border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Template Calendari</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Seleziona un template da copiare nel tuo tenant
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {templates.map((template) => (
              <div key={template.id} className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/20">
                <div>
                  <h4 className="font-medium text-foreground">{template.name}</h4>
                  <p className="text-sm text-muted-foreground">{template.holidays.length} festività</p>
                </div>
                <Button
                  onClick={() => copyTemplate(template.id)}
                  disabled={copyingTemplate === template.id}
                  size="sm"
                >
                  {copyingTemplate === template.id ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  Copia
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit/View Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card text-card-foreground border-border shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-foreground text-xl">
              {modalType === 'create' ? 'Nuovo Calendario Festività' :
               modalType === 'edit' ? `Modifica: ${formData.name}` :
               `Dettagli: ${formData.name}`}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {modalType === 'view' ? 'Visualizza i dettagli del calendario' : 'Configura le festività e le date speciali'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-foreground">Nome *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Es. Festività Italiane 2025"
                disabled={modalType === 'view'}
                className="bg-muted/30 border-border"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-foreground">Descrizione</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrizione opzionale..."
                disabled={modalType === 'view'}
                className="bg-muted/30 border-border min-h-[80px]"
              />
            </div>

            {/* Enabled Switch */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border">
              <div>
                <Label className="text-foreground">Stato</Label>
                <p className="text-xs text-muted-foreground">Abilita o disabilita questo calendario</p>
              </div>
              <Switch
                checked={formData.enabled ?? true}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enabled: checked }))}
                disabled={modalType === 'view'}
              />
            </div>

            {/* Holidays List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-foreground">Festività ({(formData.holidays || []).length})</Label>
                {modalType !== 'view' && (
                  <Button variant="outline" size="sm" onClick={loadItalianPreset}>
                    <Download className="h-4 w-4 mr-2" />
                    Carica Festività Italiane
                  </Button>
                )}
              </div>

              {/* Add Holiday Form */}
              {modalType !== 'view' && (
                <div className="flex items-end gap-2 p-3 rounded-lg bg-muted/10 border border-border">
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground">Data</Label>
                    <Input
                      type="date"
                      value={newHoliday.date || ''}
                      onChange={(e) => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
                      className="bg-muted/30 border-border"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground">Nome</Label>
                    <Input
                      value={newHoliday.name || ''}
                      onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Es. Natale"
                      className="bg-muted/30 border-border"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newHoliday.recurring ?? true}
                      onCheckedChange={(checked) => setNewHoliday(prev => ({ ...prev, recurring: checked }))}
                    />
                    <span className="text-xs text-muted-foreground">Annuale</span>
                  </div>
                  <Button onClick={addHoliday} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Holidays List */}
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {(formData.holidays || []).map((h, index) => (
                  <div key={index} className="flex items-center justify-between p-2 rounded bg-muted/30 border border-border text-sm">
                    <div className="flex items-center gap-3">
                      <CalendarDays className="h-4 w-4 text-amber-500" />
                      <span className="font-medium text-foreground">{h.name}</span>
                      <span className="font-mono text-muted-foreground">{formatDate(h.date)}</span>
                      {h.recurring && (
                        <Badge variant="outline" className="text-[9px] h-4">Annuale</Badge>
                      )}
                    </div>
                    {modalType !== 'view' && (
                      <Button variant="ghost" size="icon" onClick={() => removeHoliday(index)} className="h-6 w-6 text-destructive">
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Modal Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              {modalType === 'view' ? 'Chiudi' : 'Annulla'}
            </Button>
            {modalType !== 'view' && (
              <Button
                onClick={modalType === 'create' ? handleCreate : handleEdit}
                disabled={loading}
                className="bg-primary hover:bg-primary/90"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                {modalType === 'create' ? 'Crea' : 'Salva'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
